import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MonitoringService {
  private appInsights: ApplicationInsights;
  private routerSubscription: Subscription;

  constructor(private router: Router) {}

  init(instrumentationKey: string, userId: string): void {
    if (!instrumentationKey) {
      console.log('AppInsights instrumentationKey is not provided to initialize service!');
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey
      }
    });
    this.appInsights.loadAppInsights();

    this.routerSubscription = this.router.events
      .pipe(
        filter<ResolveEnd>(event => event instanceof ResolveEnd)
      )
      .subscribe((event: ResolveEnd) => {
        const activatedComponent = this.getActivatedComponent(event.state.root);
        if (activatedComponent) {
          this.logPageView(
            `${activatedComponent.name} ${this.getRouteTemplate(
              event.state.root
            )}`,
            event.urlAfterRedirects
          );
        }
      });

    this.setAuthenticatedUserId(userId);
    this.loadCustomTelemetryProperties(instrumentationKey);
  }

  setAuthenticatedUserId(userId: string): void {
    this.appInsights.setAuthenticatedUserContext(userId);
  }

  loadCustomTelemetryProperties(instrumentationKey: string): void {
    const telemetryInitializer = (envelope) => {
      envelope.tags["ai.cloud.role"] = "Front-End";
      envelope.tags["ai.cloud.roleInstance"] = instrumentationKey;
    }
    this.appInsights.addTelemetryInitializer(telemetryInitializer);
  }

  logPageView(
    name: string,
    url?: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number },
    duration?: number
  ): void {
    if (this.appInsights) {
      this.appInsights.trackPageView({
        name,
        uri: url,
        properties: {
          ...this.AddGlobalProperties(properties),
          duration
        },
        measurements
      });
    }
  }

  logEvent(
    name: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number }
  ): void {
    if (this.appInsights) {
      this.appInsights.trackEvent({
        name,
        properties: {
          ...this.AddGlobalProperties(properties)
        },
        measurements
      });
    }
  }

  logError(
    error: Error,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number }
  ): void {
    if (this.appInsights) {
      this.appInsights.trackException({
        error,
        properties: {
          ...this.AddGlobalProperties(properties)
        },
        measurements
      });
    }
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  private AddGlobalProperties(properties?: {
    [key: string]: string;
  }): { [key: string]: string } {
    if (!properties) {
      properties = {};
    }

    // add your custom properties such as app version

    return properties;
  }
}
